import { Link, useParams } from "react-router-dom";
import PageShell from "@/components/feature/PageShell";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import FavoriteButton from "@/components/base/FavoriteButton";
import ShareBar from "@/components/base/ShareBar";
import SectionHeading from "@/components/base/SectionHeading";
import CharacterCard from "@/components/feature/CharacterCard";
import { useSeo } from "@/hooks/useSeo";
import { charactersList, getCharacterById } from "@/mocks/characters";
import { getWorkById } from "@/mocks/works";

export default function CharacterDetailPage() {
  const { id } = useParams();
  const character = getCharacterById(id);

  useSeo(
    character ? `${character.name}（${character.workTitle}）角色分析 - age动漫` : "角色专题未找到 - age动漫",
    character
      ? `${character.name}角色分析：${character.summary} 从人物弧线、动机与叙事功能出发的深度解读。`
      : "该角色专题可能已被移除或链接有误，返回角色分析专题浏览更多内容。",
    character ? `${character.name},${character.workTitle},角色分析,age动漫,动漫角色` : "age动漫,角色未找到",
  );

  if (!character) {
    return (
      <PageShell>
        <div className="w-full px-4 md:px-6 lg:px-10 py-10">
          <Breadcrumb
            items={[{ label: "首页", to: "/" }, { label: "角色专题", to: "/characters" }, { label: "未找到" }]}
          />
          <div className="mt-6 max-w-2xl mx-auto">
            <EmptyState
              title="角色专题未找到"
              description="这篇角色分析可能已被移除，或者链接地址有误。可以返回角色专题页浏览其它解析内容。"
              icon="ri-user-search-line"
              action={
                <div className="flex flex-wrap items-center justify-center gap-2">
                  <Link
                    to="/characters"
                    className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-arrow-left-line"></i>
                    返回角色专题
                  </Link>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md border border-background-300 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    回到首页
                  </Link>
                </div>
              }
            />
          </div>
        </div>
      </PageShell>
    );
  }

  const work = getWorkById(character.workId);
  const related = charactersList
    .filter((item) => item.id !== character.id)
    .sort((a, b) => {
      const aScore = (a.workId === character.workId ? 2 : 0) + (a.archetype === character.archetype ? 1 : 0);
      const bScore = (b.workId === character.workId ? 2 : 0) + (b.archetype === character.archetype ? 1 : 0);
      return bScore - aScore;
    })
    .slice(0, 3);

  return (
    <PageShell>
      <section className="w-full bg-background-100 border-b border-background-200">
        <div className="w-full px-4 md:px-6 lg:px-10 py-6 md:py-9">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "角色专题", to: "/characters" },
              { label: character.name },
            ]}
          />
          <div className="mt-4 md:mt-5 flex flex-col md:flex-row gap-5 md:gap-7">
            <div className="w-40 sm:w-48 md:w-56 shrink-0 aspect-square rounded-lg overflow-hidden bg-background-200">
              <img
                src={character.avatar}
                alt={`${character.name} 角色立绘 - ${character.workTitle} - age动漫`}
                title={`${character.name} - ${character.workTitle} 角色分析`}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className="px-2 py-0.5 rounded bg-primary-500 text-background-50 text-[11px]">
                  {character.archetype}
                </span>
                <Link
                  to={`/works/detail/${character.workId}`}
                  className="px-2 py-0.5 rounded bg-background-50 border border-background-200 text-[11px] text-accent-600 hover:border-accent-300 transition-colors cursor-pointer"
                >
                  《{character.workTitle}》
                </Link>
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 leading-tight">{character.name}</h1>
              <p className="mt-1 text-[11px] tracking-[0.16em] text-foreground-400 uppercase">
                {character.romanizedName}
              </p>
              <blockquote className="mt-3 border-l-2 border-primary-300 pl-3 text-sm text-foreground-600 leading-relaxed">
                {character.quote}
              </blockquote>
              <div className="mt-3 flex flex-wrap gap-2">
                {character.traits.map((trait) => (
                  <span key={trait} className="px-2 py-1 rounded bg-secondary-100 text-secondary-900 text-[11px]">
                    {trait}
                  </span>
                ))}
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <FavoriteButton type="characters" id={character.id} title={character.name} />
                <span className="text-xs text-foreground-500">更新于 {character.updatedAt}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="w-full px-4 md:px-6 lg:px-10 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] gap-8 lg:gap-10">
          <div className="min-w-0 flex flex-col gap-8 md:gap-10">
            <section aria-labelledby="character-analysis-heading">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-5 h-[3px] rounded-full bg-primary-500"></span>
                <h2 id="character-analysis-heading" className="text-base md:text-lg font-semibold text-foreground-950">
                  角色分析
                </h2>
              </div>
              <div className="space-y-4">
                {character.analysis.map((paragraph, index) => (
                  <p key={index} className="text-[15px] leading-7 md:leading-8 text-foreground-700">
                    {paragraph}
                  </p>
                ))}
              </div>
              <div className="mt-5 rounded-lg border border-secondary-100 bg-secondary-50 p-4">
                <p className="text-xs text-foreground-600 leading-relaxed">
                  本文包含对作品剧情的讨论，建议在观看对应作品后阅读。角色分析仅代表作者观点。
                </p>
              </div>
            </section>

            {work && (
              <section aria-labelledby="character-work-heading">
                <h2 id="character-work-heading" className="text-base md:text-lg font-semibold text-foreground-950 mb-3">
                  关联作品
                </h2>
                <Link
                  to={`/works/detail/${work.id}`}
                  className="group flex flex-col sm:flex-row gap-4 rounded-lg border border-background-200 bg-background-50 p-3 md:p-4 hover:border-primary-200 transition-colors cursor-pointer"
                >
                  <div className="w-full sm:w-28 aspect-[4/5] sm:aspect-[4/5] max-h-44 sm:max-h-none rounded-md overflow-hidden bg-background-200 shrink-0">
                    <img
                      src={work.cover}
                      alt={`${work.title} 海报 - age动漫作品介绍`}
                      title={`${work.title} - 作品详情`}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-1.5">
                      <span className="px-2 py-0.5 rounded bg-accent-50 text-accent-600 text-[11px]">{work.type}</span>
                      <span className="text-[11px] text-foreground-400">
                        {work.year} · {work.region} · {work.episodes}
                      </span>
                    </div>
                    <h3 className="text-base md:text-lg font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors">
                      {work.title}
                    </h3>
                    <p className="mt-1.5 text-sm text-foreground-500 leading-relaxed line-clamp-3">{work.summary}</p>
                    <span className="inline-flex items-center gap-1 mt-2.5 text-sm text-primary-600 font-medium">
                      查看作品详情
                      <i className="ri-arrow-right-line transition-transform group-hover:translate-x-0.5"></i>
                    </span>
                  </div>
                </Link>
              </section>
            )}

            <section>
              <SectionHeading kicker="RELATED" title="相关角色推荐" actionLabel="全部角色" actionTo="/characters" />
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
                {related.map((item) => (
                  <CharacterCard key={item.id} item={item} />
                ))}
              </div>
            </section>
          </div>

          <aside className="lg:sticky lg:top-24 h-fit flex flex-col gap-4">
            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <h2 className="text-sm font-semibold text-foreground-900 mb-3">角色信息</h2>
              <dl className="flex flex-col gap-2.5">
                <div className="flex items-center justify-between gap-3 text-xs">
                  <dt className="text-foreground-400">所属作品</dt>
                  <dd className="text-foreground-800 text-right">{character.workTitle}</dd>
                </div>
                <div className="flex items-center justify-between gap-3 text-xs">
                  <dt className="text-foreground-400">角色类型</dt>
                  <dd className="text-foreground-800 text-right">{character.archetype}</dd>
                </div>
                <div className="flex items-center justify-between gap-3 text-xs">
                  <dt className="text-foreground-400">专题更新</dt>
                  <dd className="text-foreground-800 text-right">{character.updatedAt}</dd>
                </div>
              </dl>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <h2 className="text-sm font-semibold text-foreground-900 mb-3">收藏与分享</h2>
              <div className="flex flex-wrap gap-2">
                <FavoriteButton type="characters" id={character.id} title={character.name} />
              </div>
              <div className="mt-3 pt-3 border-t border-background-100">
                <ShareBar />
              </div>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <h2 className="text-sm font-semibold text-foreground-900 mb-3">相关角色</h2>
              <ul className="flex flex-col gap-3">
                {related.map((item) => (
                  <li key={item.id}>
                    <Link to={`/characters/detail/${item.id}`} className="group flex gap-2.5 cursor-pointer">
                      <div className="w-10 h-10 rounded-full overflow-hidden bg-background-200 shrink-0">
                        <img
                          src={item.avatar}
                          alt={`${item.name} 头像`}
                          title={`${item.name} - 角色分析`}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-foreground-800 group-hover:text-primary-600 transition-colors">
                          {item.name}
                        </p>
                        <p className="text-[10px] text-foreground-400 mt-0.5 truncate">
                          {item.workTitle} · {item.archetype}
                        </p>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
              <Link
                to="/characters"
                className="mt-4 flex items-center justify-center gap-1.5 rounded-md border border-background-300 px-3 py-2 text-xs text-foreground-600 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                返回角色专题
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </PageShell>
  );
}